import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useMemo } from 'react'
import { Button, LibraryDialog, type MediaAsset } from '../../../ui'
import { assetHubOpenAtom, assetHubPromiseAtom, assetHubSelectedAssetIdAtom } from '../atoms'
import { useAssetHubStore } from '../store'
import { useAssetHubData } from '../useAssetHubData'
import { AssetGrid } from './asset-grid'

export function AssetHubPicker() {
	const store = useAssetHubStore()
	const [isOpen, setIsOpen] = useAtom(assetHubOpenAtom, { store })
	const [promise, setPromise] = useAtom(assetHubPromiseAtom, { store })

	const handleClose = useCallback(() => {
		promise?.resolve(undefined)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	const handleSelect = useCallback((asset: MediaAsset) => {
		promise?.resolve(asset)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	return (
		<LibraryDialog open={isOpen} onClose={handleClose}>
			{isOpen
				? (
					<AssetHubPickerContent
						onSelect={handleSelect}
						onClose={handleClose}
					/>
				)
				: null}
		</LibraryDialog>
	)
}

function AssetHubPickerContent({
	onSelect,
	onClose,
}: {
	onSelect: (asset: MediaAsset) => void
	onClose: () => void
}) {
	const store = useAssetHubStore()
	const { assets } = useAssetHubData()
	const selectedAssetId = useAtomValue(assetHubSelectedAssetIdAtom, { store })

	const selectedAsset = useMemo(
		() => assets.find((a) => a.id === selectedAssetId),
		[assets, selectedAssetId],
	)

	const handleSelectClick = useCallback(() => {
		if (selectedAsset) {
			onSelect(selectedAsset)
		}
	}, [selectedAsset, onSelect])

	return (
		<AssetGrid
			mode="picker"
			showCloseButton={true}
			onClose={onClose}
			committalBar={
				<div className="flex justify-end p-4">
					<Button
						size="normal"
						type="committal"
						onClick={handleSelectClick}
						disabled={!selectedAsset}
					>
						Select This Asset
					</Button>
				</div>
			}
		/>
	)
}
